import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';

import { Flag } from '../../pages/Home/HomeHeader/LanguagePill';
import { KICKER, PILL_PRIMARY, cx } from '../../pages/Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';
import {
    ABOUT_US_LINK,
    ABOUT_US_MEDIA_LINK,
    ABOUT_US_PARTNERS_LINK,
    ABOUT_US_TEAM_LINK,
    FEATURES_LINK,
    FOR_SCHOOLS_LINK,
    FOR_TUTORING_LINK,
    SUBSCRIPTIONS_LINK,
    TUTORIAL_LINK,
} from '../../common/urlLinks';

// The same destinations as the wide nav and its submenus, flattened for a phone.
const LINKS = [
    { to: '/', id: 'home' },
    { to: FOR_TUTORING_LINK, id: 'topMenu.forTutoring.name' },
    { to: FOR_SCHOOLS_LINK, id: 'topMenu.forSchools.name' },
    { to: SUBSCRIPTIONS_LINK, id: 'topMenu.subscriptions.name' },
    { to: FEATURES_LINK, id: 'topMenu.features.name' },
    { to: TUTORIAL_LINK, id: 'topMenu.tutorial.name' },
];
const ABOUT = [
    { to: ABOUT_US_LINK, id: 'aboutUs.ourMission.button' },
    { to: ABOUT_US_PARTNERS_LINK, id: 'aboutUs.partners.button' },
    { to: ABOUT_US_TEAM_LINK, id: 'aboutUs.team.button' },
    { to: ABOUT_US_MEDIA_LINK, id: 'aboutUs.media.button' },
];
const LANGUAGES = ['ro', 'en', 'fr'];

// The trigger and the close button share the language pill's bordered box.
const ICON_BUTTON = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '46px',
    height: '46px',
    padding: 0,
    border: `1px solid ${REDESIGN_COLORS.navBorder}`,
    borderRadius: '12px',
    background: REDESIGN_COLORS.white,
    color: REDESIGN_COLORS.navText,
    cursor: 'pointer',
    '&:hover': {
        borderColor: REDESIGN_COLORS.primary,
    },
};

const useStyles = makeStyles(() => ({
    trigger: ICON_BUTTON,
    paper: {
        boxSizing: 'border-box',
        width: 'min(100vw, 400px)',
        display: 'flex',
        flexDirection: 'column',
        background: REDESIGN_COLORS.cream,
        fontFamily: REDESIGN_FONTS.body,
    },
    top: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '68px',
        padding: '0 14px 0 18px',
        borderBottom: `1px solid ${REDESIGN_COLORS.navBottomBorder}`,
    },
    logo: {
        display: 'block',
        width: '104px',
        height: 'auto',
    },
    close: ICON_BUTTON,
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: '8px 18px 18px',
    },
    list: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
    },
    link: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '15px 2px',
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        color: REDESIGN_COLORS.dark,
        fontSize: '1.15rem',
        lineHeight: 1.25,
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        textDecoration: 'none',
        '&:hover': {
            color: REDESIGN_COLORS.link,
        },
    },
    linkOn: {
        color: REDESIGN_COLORS.link,
    },
    arrow: {
        color: REDESIGN_COLORS.primary,
        fontSize: '1.05rem',
    },
    kicker: {
        ...KICKER,
        display: 'block',
        margin: '26px 0 6px',
    },
    subLink: {
        padding: '11px 2px',
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
        color: REDESIGN_COLORS.body,
    },
    // The language switch, on the groups page's segmented toggle.
    track: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        padding: '4px',
        borderRadius: '999px',
        background: REDESIGN_COLORS.toggleTrack,
        border: `1px solid ${REDESIGN_COLORS.line}`,
    },
    toggle: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        minHeight: '40px',
        border: 0,
        borderRadius: '999px',
        background: 'none',
        color: REDESIGN_COLORS.body,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.88rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.04em',
        cursor: 'pointer',
    },
    toggleOn: {
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        boxShadow: '0 1px 3px rgba(6, 63, 69, 0.12)',
    },
    footer: {
        padding: '14px 18px calc(18px + env(safe-area-inset-bottom))',
        borderTop: `1px solid ${REDESIGN_COLORS.navBottomBorder}`,
        background: REDESIGN_COLORS.white,
    },
    login: {
        ...PILL_PRIMARY,
        width: '100%',
        minHeight: '52px',
        fontSize: '1rem',
    },
}));

const MenuGlyph = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M3 6h16M3 11h16M3 16h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const CloseGlyph = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M5 5l12 12M17 5L5 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

/**
 * The logged-out navigation on small screens: a full-height sheet from the
 * right with every destination of the wide nav, the about-us pages, the
 * language switch and the login button. Language and login handlers are the
 * header's, so logging and redirects stay as they are.
 */
const LoggedOutMobileMenu = ({ language, handleRo, handleEn, handleFr, handleOpen }) => {
    const classes = useStyles();
    const intl = useIntl();
    const location = useLocation();
    const [open, setOpen] = React.useState(false);

    const current = (language || 'ro').toLowerCase();
    const handlers = { ro: handleRo, en: handleEn, fr: handleFr };
    const close = () => setOpen(false);

    const pickLanguage = (code) => (event) => {
        close();
        const handler = handlers[code];
        if (handler) handler(event);
    };

    const login = (event) => {
        close();
        if (handleOpen) handleOpen(event);
    };

    // Public routes carry the language as a prefix (/ro, /en/features), which the links leave out.
    const path = location.pathname.replace(/^\/(ro|en|fr)(?=\/|$)/, '') || '/';
    const isCurrent = (to) => (to === '/' ? path === '/' : path.startsWith(to));

    return (
        <>
            <button
                type="button"
                className={classes.trigger}
                onClick={() => setOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={intl.formatMessage({ id: 'redesign.mobileMenu.open' })}
            >
                <MenuGlyph />
            </button>

            <Drawer anchor="right" open={open} onClose={close} classes={{ paper: classes.paper }}>
                <div className={classes.top}>
                    <img src="/VBlogo.svg" alt="Virtual Board" width={238} height={104} className={classes.logo} />
                    <button
                        type="button"
                        className={classes.close}
                        onClick={close}
                        aria-label={intl.formatMessage({ id: 'redesign.mobileMenu.close' })}
                    >
                        <CloseGlyph />
                    </button>
                </div>

                <nav className={classes.body}>
                    <ul className={classes.list}>
                        {LINKS.map(({ to, id }) => (
                            <li key={id}>
                                <Link
                                    to={to}
                                    onClick={close}
                                    className={cx(classes.link, isCurrent(to) && classes.linkOn)}
                                    aria-current={isCurrent(to) ? 'page' : undefined}
                                >
                                    <FormattedMessage id={id} />
                                    <span aria-hidden="true" className={classes.arrow}>
                                        →
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <span className={classes.kicker}>
                        <FormattedMessage id="aboutUs" />
                    </span>
                    <ul className={classes.list}>
                        {ABOUT.map(({ to, id }) => (
                            <li key={id}>
                                <Link
                                    to={to}
                                    onClick={close}
                                    className={cx(classes.link, classes.subLink, isCurrent(to) && classes.linkOn)}
                                >
                                    <FormattedMessage id={id} />
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <span className={classes.kicker}>
                        <FormattedMessage id="redesign.mobileMenu.language" />
                    </span>
                    <div className={classes.track} role="group">
                        {LANGUAGES.map((code) => (
                            <button
                                key={code}
                                type="button"
                                aria-pressed={current === code}
                                onClick={pickLanguage(code)}
                                className={cx(classes.toggle, current === code && classes.toggleOn)}
                            >
                                <Flag code={code} />
                                {code.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </nav>

                {handleOpen ? (
                    <div className={classes.footer}>
                        <button type="button" className={classes.login} onClick={login}>
                            <FormattedMessage id="home.login" />
                        </button>
                    </div>
                ) : null}
            </Drawer>
        </>
    );
};

export default LoggedOutMobileMenu;
